import { useEffect, useState } from 'react'
import { Languages } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { MODULES, useModules } from '@/hooks/useModules'

import { languageName, stayingSentences, type ImportedTextLanguage } from './imported-language'

interface ImportedLanguageWarningProps {
  /** Reads the check for the document about to be issued; called once. */
  load: () => Promise<ImportedTextLanguage>
}

/**
 * SOLY-IMPORT-003 S10, D-20: an imported line is never translated, so before a document in another
 * language is issued, say which lines (and which section titles) will keep the import's wording.
 * Shows nothing when there is nothing to say, or when the check cannot be read: it never blocks issuing.
 * Without quote_import there is no imported line, so the check is not even read (S19).
 */
export function ImportedLanguageWarning({ load }: ImportedLanguageWarningProps) {
  const { hasModule } = useModules()
  const enabled = hasModule(MODULES.QUOTE_IMPORT)
  const [check, setCheck] = useState<ImportedTextLanguage | null>(null)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    load()
      .then((result) => {
        if (!cancelled) setCheck(result)
      })
      .catch(() => {
        // The warning is advice, not a gate: without it the document still issues as before.
      })
    return () => {
      cancelled = true
    }
    // `load` is built inline by the page from route params that do not change while it is shown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  if (!enabled || !check || (check.lines.length === 0 && check.categories.length === 0)) return null

  const documentLanguage = languageName(check.language)

  return (
    <Alert variant="warning" data-testid="imported-language-warning">
      <Languages className="h-4 w-4" />
      <AlertTitle>Document en {documentLanguage}</AlertTitle>
      <AlertDescription className="space-y-2">
        {stayingSentences(check).map((sentence) => (
          <p key={sentence} className="font-medium">
            {sentence}
          </p>
        ))}
        {check.lines.length > 0 && (
          <ul className="max-h-40 overflow-y-auto list-disc pl-4 text-xs" data-testid="imported-language-lines">
            {check.lines.map((line, index) => (
              <li key={index}>{line.title}</li>
            ))}
          </ul>
        )}
        {check.categories.length > 0 && (
          <p className="text-xs" data-testid="imported-language-categories">
            Sans nom en {documentLanguage} (titre de section) : {check.categories.join(', ')}
          </p>
        )}
        <p className="text-xs">Tu peux générer quand même, ou revenir corriger ces textes avant.</p>
      </AlertDescription>
    </Alert>
  )
}
